'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Lock, User, LayoutDashboard, Smartphone } from 'lucide-react'
import { toast } from 'sonner'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CaptchaWidget } from '@/components/common/CaptchaWidget'
import { useAuthStore } from '@/stores/auth-store'
import {
  login as loginApi,
  send2faCode as send2faCodeApi,
  verify2fa as verify2faApi,
  type LoginResponse,
} from '@/lib/api/auth-api'

const loginFormSchema = z.object({
  username: z
    .string()
    .min(1, '请输入用户名')
    .min(3, '用户名至少 3 个字符')
    .max(20, '用户名最多 20 个字符'),
  password: z
    .string()
    .min(1, '请输入密码')
    .min(6, '密码至少 6 个字符')
    .max(50, '密码最多 50 个字符'),
})

type LoginFormData = z.infer<typeof loginFormSchema>

/** 短信二次验证进行中的状态（登录接口返回 twoFactor=true 时进入） */
interface TwoFactorPending {
  ticket: string
  mobileMasked: string
}

const RESEND_COUNTDOWN_SECONDS = 60

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [pendingData, setPendingData] = useState<LoginFormData | null>(null)

  // 短信二次验证状态
  const [twoFactor, setTwoFactor] = useState<TwoFactorPending | null>(null)
  const [twoFaCode, setTwoFaCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/welcome'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
  })

  // 重发倒计时（进入二次验证时首个验证码已由登录接口发出）
  useEffect(() => {
    if (resendCountdown <= 0) return
    const timer = setTimeout(() => setResendCountdown((n) => n - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCountdown])

  const onSubmit = (data: LoginFormData) => {
    setPendingData(data)
    setShowCaptcha(true)
  }

  /** 登录成功（直接登录或二次验证通过）后的统一收尾 */
  const handleLoginSuccess = async (result: LoginResponse) => {
    if (!pendingData) return
    const userInfo = {
      id: result.adminId,
      username: result.username,
      realName: result.realName,
      email: result.email,
      roleName: result.roleName,
      roleCode: result.roleCode,
      permissions: result.permissions || [],
      allowedBizLines: result.allowedBizLines || [],
    }
    login(result.token, userInfo)
    try {
      if ('PasswordCredential' in window) {
        const CredentialCtor = window.PasswordCredential as unknown as new (init: { id: string; password: string }) => Credential
        const cred = new CredentialCtor({
          id: pendingData.username,
          password: pendingData.password,
        })
        navigator.credentials.store(cred)
      }
    } catch {
      // 浏览器不支持或用户拒绝，静默忽略
    }
    toast.success('登录成功')
    navigate(from, { replace: true })
  }

  const handleCaptchaVerified = async (token: string) => {
    if (!pendingData) return
    try {
      setIsLoading(true)
      // 弹窗保持打开：若开启短信二次验证，验证码输入步将原地替换滑块内容
      const result = await loginApi({
        username: pendingData.username,
        password: pendingData.password,
        captchaVerification: token,
      })
      if (result.twoFactor && result.ticket) {
        setTwoFactor({ ticket: result.ticket, mobileMasked: result.mobileMasked || '' })
        setTwoFaCode('')
        setResendCountdown(RESEND_COUNTDOWN_SECONDS)
        return
      }
      setShowCaptcha(false)
      await handleLoginSuccess(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : '登录失败，请稍后重试'
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  /** 校验短信验证码，换取正式 token */
  const handleVerify2fa = async () => {
    if (!twoFactor || !pendingData) return
    if (!twoFaCode.trim()) {
      toast.error('请输入短信验证码')
      return
    }
    try {
      setIsVerifying(true)
      const result = await verify2faApi({ ticket: twoFactor.ticket, code: twoFaCode.trim() })
      setShowCaptcha(false)
      setTwoFactor(null)
      await handleLoginSuccess(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : '验证失败，请重试'
      toast.error(message)
    } finally {
      setIsVerifying(false)
    }
  }

  /** 重发短信验证码（后端 50 秒频控，前端 60 秒倒计时） */
  const handleResend2fa = async () => {
    if (!twoFactor || resendCountdown > 0) return
    try {
      await send2faCodeApi(twoFactor.ticket)
      setResendCountdown(RESEND_COUNTDOWN_SECONDS)
      toast.success('验证码已重新发送')
    } catch (error) {
      const message = error instanceof Error ? error.message : '发送失败，请稍后重试'
      toast.error(message)
    }
  }

  /** 关闭弹窗并重置二次验证状态 */
  const closeDialog = (open: boolean) => {
    setShowCaptcha(open)
    if (!open) {
      setTwoFactor(null)
      setTwoFaCode('')
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-neutral-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-md bg-primary-500 flex items-center justify-center">
            <LayoutDashboard className="h-6 w-6 text-white" />
          </div>
           <h1 className="mt-4 text-xl font-semibold text-neutral-900">
             远行客管理系统
           </h1>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200 p-5">
          <div className="space-y-1 pb-4 mb-4 border-b border-neutral-100">
             <h2 className="text-base font-medium text-neutral-900">登录账户</h2>
          </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label htmlFor="username" className="text-sm text-neutral-700 block mb-3">用户名</Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-neutral-400" />
                </div>
                 <Input
                   id="username"
                   type="text"
                   placeholder="请输入用户名"
                   autoComplete="username"
                   className="pl-10 h-11"
                   {...register('username')}
                 />
              </div>
              {errors.username && (
                <p className="text-sm text-red-500">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm text-neutral-700 block mb-3">密码</Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-neutral-400" />
                </div>
                 <Input
                   id="password"
                   type={showPassword ? 'text' : 'password'}
                   placeholder="请输入密码"
                   autoComplete="current-password"
                   className="pl-10 pr-10 h-11"
                   {...register('password')}
                 />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-neutral-400 hover:text-neutral-600" />
                  ) : (
                    <Eye className="h-4 w-4 text-neutral-400 hover:text-neutral-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="pt-3">
              <Button
                type="submit"
              className="w-full"
              isLoading={isLoading}
            >
              {isLoading ? '登录中...' : '登录'}
            </Button>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-neutral-400">
           © {new Date().getFullYear()} 杭州远行客网络科技有限公司. All rights reserved.
        </p>
      </div>

      {/* 短信验证码步骤禁用遮罩点击关闭（误触丢票据需重走密码+滑块）；Esc 与右上角 X 仍可正常关闭 */}
      <Dialog open={showCaptcha} onOpenChange={closeDialog} disablePointerDismissal={!!twoFactor}>
        <DialogContent size="sm">
          {twoFactor ? (
            <>
              <DialogHeader>
                <DialogTitle>短信验证</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Smartphone className="h-4 w-4 text-neutral-400" />
                  <span>验证码已发送至 {twoFactor.mobileMasked}</span>
                </div>
                <div>
                  <Label htmlFor="sms-code" className="text-sm text-neutral-700 block mb-2">短信验证码</Label>
                  <Input
                    id="sms-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="请输入 6 位验证码"
                    value={twoFaCode}
                    onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleVerify2fa()
                    }}
                    autoFocus
                    className="h-11 tracking-[0.3em]"
                  />
                </div>
                <Button
                  className="w-full"
                  isLoading={isVerifying}
                  onClick={handleVerify2fa}
                >
                  {isVerifying ? '验证中...' : '验证并登录'}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    className="text-neutral-500 hover:text-neutral-700"
                    onClick={() => closeDialog(false)}
                  >
                    返回重新登录
                  </button>
                  <button
                    type="button"
                    className="text-primary-500 hover:underline disabled:text-neutral-400 disabled:no-underline disabled:cursor-not-allowed"
                    disabled={resendCountdown > 0 || isVerifying}
                    onClick={handleResend2fa}
                  >
                    {resendCountdown > 0 ? `${resendCountdown}s 后可重发` : '重新发送验证码'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>安全验证</DialogTitle>
              </DialogHeader>
              <CaptchaWidget onVerified={handleCaptchaVerified} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
